import * as THREE from "three";
import { IVfxTask } from "../../core/runtime/vfx_task_runner.js";
import { Vfx3DRuntime } from "../../vfx_3d_runtime.js";
import { IController, StopBehavior } from "../../core/types/controller_types.js";
import { VfxPlanarWaveBatch } from "../../core/batches/vfx_planar_wave_batch.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { randomRange } from "../../../common/common_funcs.js";

export class VfxTableImpactTask implements IVfxTask, IController
{
  // --- Force Waves Constants (WaveGlow1 & WaveGlow2) ---
  private static readonly WAVES_BURST_COUNT        : number      = 8;
  private static readonly WAVES_LIFESPAN           : number      = 0.25;
  private static readonly WAVES_SPEED              : number      = 1;
  private static readonly WAVES_MIN_SPAWN_AREA_RADIUS: number    = 0;
  private static readonly WAVES_MAX_SPAWN_AREA_RADIUS: number    = 0.25;
  private static readonly WAVES_MIN_WIDTH          : number      = 0.6;  // Розмах дуги хвилі
  private static readonly WAVES_MAX_WIDTH          : number      = 1;
  private static readonly WAVES_MIN_HEIGHT         : number      = 0.28; // Товщина дуги хвилі
  private static readonly WAVES_MAX_HEIGHT         : number      = 0.38;
  private static readonly WAVES_FADE_START_PROGRESS: number      = 0.25; // Початок спадання альфи
  private static readonly WAVES_MAX_ALPHA          : number      = 0.3;
  private static readonly WAVES_COLOR              : THREE.Color = new THREE.Color( 0xFFFFFF );

  public is_active    : boolean = true;
  private position    : THREE.Vector3;
  private start_time  : number;
  private has_spawned : boolean = false;

  public constructor( position: THREE.Vector3 )
  {
    this.position   = position.clone();
    this.start_time = Vfx3DRuntime.current_time;
  }

  public setPosition( pos: THREE.Vector3 ): void
  {
    this.position.copy( pos );
  }

  public stop( _behavior?: StopBehavior ): void
  {
    this.is_active = false;
  }

  public start(): void
  {
    this.is_active = true;
  }

  public accelerate( _factor: number ): void {}

  public onUpdate( now: number ): boolean
  {
    if ( !this.is_active )
      return true;

    if ( now < this.start_time )
      return false;

    const age = now - this.start_time;

    if ( !this.has_spawned )
    {
      this.has_spawned = true;
      this.spawnWaves();
    }

    if ( age >= VfxTableImpactTask.WAVES_LIFESPAN )
    {
      this.stop();
      return true;
    }

    return false;
  }

  private static readonly scratch_pos: THREE.Vector3 = new THREE.Vector3();
  private static readonly scratch_vel: THREE.Vector3 = new THREE.Vector3();

  private spawnWaves(): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxPlanarWaveBatch );

    const count = VfxTableImpactTask.WAVES_BURST_COUNT;

    for ( let i = 0; i < count; i++ )
    {
      const angle = ( ( i + 0.5 ) / count ) * Math.PI * 2.0 + randomRange( -0.1, 0.1 );
      const spawn_dist = randomRange(
        VfxTableImpactTask.WAVES_MIN_SPAWN_AREA_RADIUS,
        VfxTableImpactTask.WAVES_MAX_SPAWN_AREA_RADIUS
      );

      VfxTableImpactTask.scratch_pos.set(
        this.position.x + Math.cos( angle ) * spawn_dist,
        this.position.y + Math.sin( angle ) * spawn_dist,
        this.position.z
      );

      VfxTableImpactTask.scratch_vel.set(
        Math.cos( angle ) * VfxTableImpactTask.WAVES_SPEED,
        Math.sin( angle ) * VfxTableImpactTask.WAVES_SPEED,
        0.0
      );

      const width  = randomRange( VfxTableImpactTask.WAVES_MIN_WIDTH, VfxTableImpactTask.WAVES_MAX_WIDTH );
      const height = randomRange( VfxTableImpactTask.WAVES_MIN_HEIGHT, VfxTableImpactTask.WAVES_MAX_HEIGHT );

      // Рандомний вибір текстури між WaveGlow1 та WaveGlow2
      const res_id = Math.random() < 0.5 ? ResourceID.WaveGlow1 : ResourceID.WaveGlow2;

      batch.spawnParticle(
        VfxTableImpactTask.scratch_pos,
        VfxTableImpactTask.scratch_vel,
        width,
        height,
        VfxTableImpactTask.WAVES_FADE_START_PROGRESS,
        this.start_time,
        VfxTableImpactTask.WAVES_LIFESPAN,
        VfxTableImpactTask.WAVES_MAX_ALPHA,
        VfxTableImpactTask.WAVES_COLOR,
        res_id
      );
    }
  }
}
